import React, { useEffect, useState, useCallback } from 'react';
import api from '../../utils/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table';
import { Lock, Unlock, ChevronLeft, ChevronRight, Building2, Mail } from 'lucide-react';

const todayStr = () => new Date().toISOString().split('T')[0];

const fmtEuro = (v) => `€ ${Number(v || 0).toFixed(2)}`;

export const AgenziaCassa = () => {
  const [data, setData] = useState(todayStr());
  const [foglio, setFoglio] = useState(null);
  const [loading, setLoading] = useState(true);
  const [chiudendo, setChiudendo] = useState(false);
  const [chiusure, setChiusure] = useState([]);
  const [compagnieInfo, setCompagnieInfo] = useState({});

  useEffect(() => {
    api.get('/agenzia/compagnie').then(res => {
      const map = {};
      (res.data || []).forEach(c => { map[c.nome] = c; });
      setCompagnieInfo(map);
    }).catch(() => {});
  }, []);

  const fetchFoglio = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/agenzia/cassa?data=${data}`);
      setFoglio(res.data);
    } catch {
      toast.error('Errore nel caricamento del foglio cassa');
    } finally {
      setLoading(false);
    }
  }, [data]);

  const fetchChiusure = useCallback(async () => {
    try {
      const res = await api.get('/agenzia/cassa/chiusure');
      setChiusure(res.data || []);
    } catch {}
  }, []);

  useEffect(() => { fetchFoglio(); }, [fetchFoglio]);
  useEffect(() => { fetchChiusure(); }, [fetchChiusure]);

  const spostaGiorno = (delta) => {
    const d = new Date(data + 'T00:00:00');
    d.setDate(d.getDate() + delta);
    setData(d.toISOString().split('T')[0]);
  };

  const chiudiGiornata = async () => {
    setChiudendo(true);
    try {
      await api.post('/agenzia/cassa/chiudi', { data });
      toast.success('Giornata chiusa correttamente');
      fetchFoglio();
      fetchChiusure();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Errore nella chiusura');
    } finally {
      setChiudendo(false);
    }
  };

  const riapriGiornata = async () => {
    if (!foglio?.chiusura) return;
    try {
      await api.delete(`/agenzia/cassa/chiusure/${foglio.chiusura.id}`);
      toast.success('Giornata riaperta');
      fetchFoglio();
      fetchChiusure();
    } catch {
      toast.error('Errore nella riapertura');
    }
  };

  return (
    <div className="p-4 md:p-6 space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Foglio Cassa</h1>
          <p className="text-sm text-slate-500">Chiusura cassa giornaliera per compagnia</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={() => spostaGiorno(-1)}><ChevronLeft size={16} /></Button>
          <Input type="date" value={data} onChange={(e) => setData(e.target.value)} className="w-40" />
          <Button variant="outline" size="icon" onClick={() => spostaGiorno(1)}><ChevronRight size={16} /></Button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12 text-slate-400">Caricamento...</div>
      ) : foglio && (
        <>
          <div className="flex items-center justify-between bg-white rounded-xl border border-slate-200 p-4">
            <div>
              <p className="text-sm text-slate-500">Totale incassato il {new Date(data + 'T00:00:00').toLocaleDateString('it-IT')}</p>
              <p className="text-3xl font-bold text-slate-900">{fmtEuro(foglio.totale_generale)}</p>
              <p className="text-xs text-slate-400">{foglio.numero_polizze_totale} polizze incassate</p>
            </div>
            {foglio.chiusura ? (
              <div className="text-right">
                <Badge className="bg-slate-800 text-white mb-2"><Lock size={12} className="mr-1 inline" />Giornata chiusa</Badge>
                <p className="text-xs text-slate-500">da {foglio.chiusura.chiusa_da} - {new Date(foglio.chiusura.chiusa_at).toLocaleString('it-IT')}</p>
                <Button variant="outline" size="sm" className="mt-2" onClick={riapriGiornata}>
                  <Unlock size={14} className="mr-1" /> Riapri giornata
                </Button>
              </div>
            ) : (
              <Button onClick={chiudiGiornata} disabled={chiudendo || foglio.numero_polizze_totale === 0} data-testid="agenzia-chiudi-cassa">
                <Lock size={16} className="mr-2" /> {chiudendo ? 'Chiusura...' : 'Chiudi giornata'}
              </Button>
            )}
          </div>

          <div className="grid gap-4">
            {foglio.per_compagnia.length === 0 ? (
              <div className="text-center py-12 text-slate-400 bg-white rounded-xl border border-slate-200">Nessuna polizza incassata in questa data</div>
            ) : foglio.per_compagnia.map((g) => {
              const info = compagnieInfo[g.compagnia];
              return (
              <div key={g.compagnia} className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                <div className="flex items-center justify-between px-4 py-3 bg-slate-50 border-b border-slate-200">
                  <div className="flex items-center gap-2 font-semibold text-slate-800">
                    {info?.logo_data_url ? (
                      <img src={info.logo_data_url} alt={g.compagnia} className="w-6 h-6 object-contain" />
                    ) : (
                      <Building2 size={16} />
                    )}
                    {g.compagnia}
                    {info?.email && (
                      <a href={`mailto:${info.email}?subject=${encodeURIComponent('Foglio Cassa ' + data + ' - ' + g.compagnia)}`}
                        title={`Invia foglio cassa a ${info.email}`} className="text-slate-400 hover:text-blue-600">
                        <Mail size={14} />
                      </a>
                    )}
                  </div>
                  <div className="text-sm text-slate-600">{g.numero_polizze} polizze - <span className="font-bold text-slate-900">{fmtEuro(g.totale_premio)}</span></div>
                </div>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Cliente</TableHead>
                      <TableHead>Ramo / Targa</TableHead>
                      <TableHead>N. Polizza</TableHead>
                      <TableHead>Premio</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {g.polizze.map((p) => (
                      <TableRow key={p.id}>
                        <TableCell>{p.cliente_nome} {p.cliente_cognome}</TableCell>
                        <TableCell>{p.ramo}{p.targa ? ` - ${p.targa}` : ''}</TableCell>
                        <TableCell>{p.numero_polizza || '-'}</TableCell>
                        <TableCell>{fmtEuro(p.premio)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              );
            })}
          </div>
        </>
      )}

      {chiusure.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <Label className="text-slate-500 text-xs uppercase tracking-wide">Ultime chiusure</Label>
          <div className="flex flex-wrap gap-2 mt-2">
            {chiusure.slice(0, 10).map((c) => (
              <button key={c.id} onClick={() => setData(c.data)}
                className="text-xs px-3 py-1.5 rounded-full border border-slate-200 hover:bg-slate-50">
                {new Date(c.data + 'T00:00:00').toLocaleDateString('it-IT')} - {fmtEuro(c.totale_generale)}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
